/**
 * Tapete Green Village Mobile Homes — campo creme, moldura verde dupla e a marca
 * da empresa ao centro.
 *
 * Vive FORA do `officeArt.ts` de propósito: aquele ficheiro tem 3500 linhas e é
 * mexido por várias frentes ao mesmo tempo; aqui a pegada no ficheiro disputado
 * reduz-se a duas linhas (o import e a entrada do registo).
 *
 * MEDIDA: o `rug` é um prop PLANO (`FLAT_PROPS` no `OfficeSpace.tsx`, colocado
 * com `setOrigin(0, 0)`), 4×3 tiles = **128×96 px de ecrã**. Com `detail: 2` a
 * célula do pincel é exactamente 1 px de ecrã, portanto as coordenadas de arte
 * aqui SÃO pixéis de ecrã e nada pode sair de 0..127 × 0..95 — um prop plano não
 * transborda como um prop com origem no pé.
 *
 * A marca não foi desenhada à mão: é o logótipo real reamostrado a partir da
 * arte de referência para uma máscara de 56×47. Casa CHEIA com o traço branco
 * RECORTADO lá dentro — o oposto de uma casa em contorno — mais as duas folhas
 * do canto superior direito.
 */

export interface Brush {
  r(x: number, y: number, w: number, h: number, color: string): void;
  dither(x: number, y: number, w: number, h: number, a: string, b: string): void;
}

// ── Marca ───────────────────────────────────────────────────

/**
 * Logótipo Green Village reamostrado do original (bloco verde de 405×351 px)
 * para 56×47 — a mesma proporção 1,19 do bloco de origem. `#` = verde, `.` =
 * campo. Os dois pixéis soltos (linhas 15 e 34) são a ponta do pé do traço e o
 * pedúnculo da folha pequena: estão no original, não são ruído de reamostragem.
 */
const MARCA = [
  "..................................................##....",
  "..................................................##....",
  ".................................................####...",
  "....................................##..........#####...",
  "...........................##........###......#######...",
  "..........................####.......####....####.###...",
  ".........................######......##.#...#########...",
  ".......................#########......###..#####.###....",
  "......................###########.....##...#####.###....",
  "...........#####.....#############.........####.####....",
  "...........#####....################......###..####.....",
  "...........#####..###################.........#####.....",
  "...........#####.######################......#####......",
  "...........####################.............####........",
  "...........################................###..........",
  "...........############..................#..............",
  "...........###########.................#####............",
  "...........#########................#########...........",
  ".........##########...............############..........",
  "........##########..............################........",
  "......###########..............##################.......",
  ".....###########..............####################......",
  "....###########.............########################....",
  "...###########............#######.......#############...",
  "..############...........######..........#############..",
  ".############...........#####............##############.",
  "#############..........#####.............###############",
  "..........##..........####..............######..........",
  "..........##.........###................######..........",
  "..........#.........###................#######..........",
  "..........#........###.................#######..........",
  "..........#........##.................########..........",
  "..........#.......##.................#########..........",
  "..........#.......#.................##########..........",
  ".................#.................###########..........",
  ".................................#############..........",
  "...............................###############..........",
  "............................##################..........",
  ".........................#####################..........",
  "......................########################..........",
  "...................###########################..........",
  ".................#############################..........",
  "...............###############################..........",
  "..............################################..........",
  ".............#################################..........",
  ".............#################################..........",
  "............#################################...........",
] as const;

const MARCA_W = MARCA[0].length;

// ── Tipografia ──────────────────────────────────────────────

type Fonte = Readonly<Record<string, readonly string[]>>;

/**
 * Caixa alta condensada de haste dupla, 8 px de altura — o peso de "GREEN
 * VILLAGE" no original (que mede 862×87, rácio 9,9:1; 84×8 dá 10,5:1).
 */
const FONTE_LARGA: Fonte = {
  A: [".####.", "##..##", "##..##", "##..##", "######", "##..##", "##..##", "##..##"],
  E: ["######", "##....", "##....", "#####.", "##....", "##....", "##....", "######"],
  G: [".####.", "##..##", "##....", "##....", "##.###", "##..##", "##..##", ".####."],
  I: ["##", "##", "##", "##", "##", "##", "##", "##"],
  L: ["##....", "##....", "##....", "##....", "##....", "##....", "##....", "######"],
  N: ["##..##", "###.##", "###.##", "##.###", "##.###", "##..##", "##..##", "##..##"],
  R: ["#####.", "##..##", "##..##", "##..##", "#####.", "##.##.", "##..##", "##..##"],
  V: ["##..##", "##..##", "##..##", "##..##", "##..##", ".####.", ".####.", "..##.."],
  " ": ["....", "....", "....", "....", "....", "....", "....", "...."],
};

/** Caixa alta fina de haste simples, 5 px — a linha "MOBILE HOMES", muito espaçada. */
const FONTE_ESTREITA: Fonte = {
  B: ["##.", "#.#", "##.", "#.#", "##."],
  E: ["###", "#..", "##.", "#..", "###"],
  H: ["#.#", "#.#", "###", "#.#", "#.#"],
  I: ["#", "#", "#", "#", "#"],
  L: ["#..", "#..", "#..", "#..", "###"],
  M: ["#...#", "##.##", "#.#.#", "#...#", "#...#"],
  O: [".##.", "#..#", "#..#", "#..#", ".##."],
  S: ["###", "#..", "###", "..#", "###"],
  " ": ["...", "...", "...", "...", "..."],
};

function medirTexto(f: Fonte, texto: string, esp: number): number {
  let w = -esp;
  for (const ch of texto) w += (f[ch]?.[0]?.length ?? 0) + esp;
  return w;
}

// ── Pintura ─────────────────────────────────────────────────

/**
 * Emite uma máscara de `#`/`.` como corridas contíguas — um `b.r` por corrida em
 * vez de um por pixel. Serve tanto os glifos como o logótipo.
 */
function pintarMascara(b: Brush, linhas: readonly string[], x0: number, y0: number, cor: string): void {
  for (let r = 0; r < linhas.length; r += 1) {
    const linha = linhas[r] ?? "";
    let i = 0;
    while (i < linha.length) {
      if (linha[i] !== "#") {
        i += 1;
        continue;
      }
      let j = i;
      while (j < linha.length && linha[j] === "#") j += 1;
      b.r(x0 + i, y0 + r, j - i, 1, cor);
      i = j;
    }
  }
}

function escrever(b: Brush, f: Fonte, texto: string, x: number, y: number, esp: number, cor: string): void {
  let cx = x;
  for (const ch of texto) {
    const g = f[ch];
    if (!g) continue;
    pintarMascara(b, g, cx, y, cor);
    cx += (g[0]?.length ?? 0) + esp;
  }
}

function moldura(b: Brush, x: number, y: number, w: number, h: number, e: number, cor: string): void {
  b.r(x, y, w, e, cor);
  b.r(x, y + h - e, w, e, cor);
  b.r(x, y + e, e, h - 2 * e, cor);
  b.r(x + w - e, y + e, e, h - 2 * e, cor);
}

/**
 * Paleta lida directamente da arte de referência: o campo é CREME tecido (não
 * branco puro) e a moldura é verde escuro com um filete verde-oliva por dentro.
 */
export function drawRug(b: Brush): void {
  const verde = "#348434";
  const verdeFilete = "#79a240";
  const creme = "#dfcdb0";
  const cremeHi = "#e3d2b6";

  const W = 128;
  const H = 96;

  // Moldura: 3 px de verde escuro até à aresta + 1 px de filete claro por dentro.
  b.r(0, 0, W, H, verde);
  moldura(b, 3, 3, W - 6, H - 6, 1, verdeFilete);

  // Campo tecido: base creme com um friso mais claro de 3 em 3 linhas — é o que
  // dá a nervura horizontal do original sem virar riscas.
  b.r(4, 4, W - 8, H - 8, creme);
  for (let y = 6; y < H - 4; y += 3) b.r(4, y, W - 8, 1, cremeHi);

  // Composição vertical dentro do campo (y 4..91, 88 px), nas proporções do
  // original: 9 · marca 47 · 5 · GREEN VILLAGE 8 · 4 · MOBILE HOMES 5 · 10.
  pintarMascara(b, MARCA, (W - MARCA_W) >> 1, 13, verde);

  const largo = medirTexto(FONTE_LARGA, "GREEN VILLAGE", 1);
  const estreito = medirTexto(FONTE_ESTREITA, "MOBILE HOMES", 2);
  escrever(b, FONTE_LARGA, "GREEN VILLAGE", (W - largo) >> 1, 65, 1, verde);
  escrever(b, FONTE_ESTREITA, "MOBILE HOMES", (W - estreito) >> 1, 77, 2, verde);
}
